/**
 * SQUID GAME - THE SLAP BRIDGE
 * 
 * High-risk gambling mode inspired by Squid Game's glass bridge.
 * 7 Steps, 50/50 chance each step.
 * Win = Multiplier rewards
 * Fail = Entry fee goes to House Treasury (NO BURN)
 */

import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Skull, Trophy, Zap, AlertTriangle, ArrowRight, 
  DollarSign, Flame, Play, RotateCcw, Home, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useAuth } from '../providers/AuthProvider';
import { useGameWallet } from '../providers/GameWalletProvider';
import { usePriceOracle } from '../providers/PriceOracleProvider';
import { transferToTreasury, checkSlapBalance } from '../services/tokenTransferService';
import confetti from 'canvas-confetti';

// ============================================================
// SQUID GAME CONFIG - NEW RISK MANAGEMENT MULTIPLIERS
// ============================================================
const TOTAL_STEPS = 7;
const BASE_USD_ENTRY = 2.00; // $2.00 USD entry fee

// NEW PAYOUT LADDER (Risk Management V1.2):
// Step 1: 0x (No win yet - must continue)
// Step 2: 1.2x (Small win - can cash out)
// Step 3: 0x (Safety step/Reset - no payout)
// Step 4: 2.0x (Double - can cash out)
// Step 5: 0x (No payout)
// Step 6: 0x (No payout)
// Step 7: 4.0x (Jackpot - END)
// Losing at ANY step = House keeps entry. High risk!
const MULTIPLIERS = [0, 1.2, 0, 2.0, 0, 0, 4.0];

// ============================================================
// TREASURY WALLET - ALL LOSSES/FEES GO HERE (BUSINESS MODEL)
// ============================================================
const TREASURY_WALLET = 'GntfPdLmhjJ2unKJ9fp7S6rDz7AA1Hkn52pKpd9TceCa';

// Glass panel types
const PANEL_SAFE = 'safe';
const PANEL_TRAP = 'trap';

const SquidGameBridge = () => {
  const navigate = useNavigate();
  const { user, walletAddress, secretKey, solanaAddress, localWallet } = useAuth();
  const { slapBalance, spendCredits, addCredits, fetchSlapTokenBalance, connectionStatus } = useGameWallet();
  const { tokenPrice, isLive } = usePriceOracle();
  
  // ============================================================
  // CRITICAL: SANITIZE BALANCE - Handle string/number type mismatch
  // slapBalance might be a formatted string like "15.000.000" or a number
  // We need a clean NUMBER for math comparisons
  // ============================================================
  const sanitizeBalance = (balance) => {
    if (typeof balance === 'number') return balance;
    if (typeof balance === 'string') {
      // Remove all dots and commas, then parse as float
      const cleaned = balance.replace(/[.,\s]/g, '');
      return parseFloat(cleaned) || 0;
    }
    return 0;
  };
  
  // Use sanitized balance for ALL math operations
  const userBalance = sanitizeBalance(slapBalance);
  
  // Game state
  const [gameState, setGameState] = useState('lobby'); // lobby, playing, won, lost
  const [currentStep, setCurrentStep] = useState(0);
  const [selectedPanel, setSelectedPanel] = useState(null);
  const [revealedPanels, setRevealedPanels] = useState({}); // step -> 'left' or 'right' (safe one)
  const [isRevealing, setIsRevealing] = useState(false);
  const [entryFeePaid, setEntryFeePaid] = useState(0);
  const [avatarPosition, setAvatarPosition] = useState('center'); // 'center', 'left', 'right'
  const [showShatter, setShowShatter] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState('🐸'); // Player avatar
  
  // Payment state - for real on-chain transfer
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentProgress, setPaymentProgress] = useState(null);
  const [entryTxSignature, setEntryTxSignature] = useState(null);
  
  // Available avatars
  const AVATARS = ['🐸', '🥷', '🤠', '👻', '🦊', '🐱'];
  
  // ============================================================
  // HOUSE FEE - Treasury keeps 10% of every payout
  // ============================================================
  const HOUSE_FEE_PERCENT = 10;
  
  // Dynamic entry fee in $SLAP (based on $2 USD)
  const [entryFeeSlap, setEntryFeeSlap] = useState(0);
  const [lastPriceUpdate, setLastPriceUpdate] = useState(null);
  
  // Calculate entry fee in $SLAP based on token price
  useEffect(() => {
    if (tokenPrice > 0) {
      const slapAmount = Math.ceil(BASE_USD_ENTRY / tokenPrice);
      setEntryFeeSlap(slapAmount);
      setLastPriceUpdate(new Date());
    }
  }, [tokenPrice]);
  
  // Refresh price every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      // Price updates automatically via PriceOracleProvider
      setLastPriceUpdate(new Date());
    }, 5000);
    return () => clearInterval(interval);
  }, []);
  
  // Calculate potential winnings at each step (AFTER house fee)
  const getPotentialWin = (step) => {
    if (step === 0) return entryFeeSlap;
    const grossWin = Math.floor(entryFeeSlap * MULTIPLIERS[step - 1]);
    const houseFee = Math.floor(grossWin * (HOUSE_FEE_PERCENT / 100));
    return grossWin - houseFee; // Net payout after 10% house fee
  };
  
  // Get gross potential (before fee) for display
  const getGrossPotentialWin = (step) => {
    if (step === 0) return entryFeeSlap;
    return Math.floor(entryFeeSlap * MULTIPLIERS[step - 1]);
  };
  
  // Get current multiplier
  const getCurrentMultiplier = () => {
    if (currentStep === 0) return 1;
    return MULTIPLIERS[currentStep - 1];
  };
  
  // Start game - Entry fee goes to TREASURY (REAL ON-CHAIN TRANSFER)
  // CRITICAL: Use sanitized userBalance for ALL math comparisons
  const startGame = async () => {
    // Debug log to trace the issue
    console.log('[SquidGame] Balance Check:', {
      rawSlapBalance: slapBalance,
      sanitizedUserBalance: userBalance,
      entryFeeSlap: entryFeeSlap,
      typeOfRaw: typeof slapBalance,
      comparison: userBalance < entryFeeSlap
    });
    
    // Check balance using SANITIZED userBalance (handles string/number mismatch)
    if (userBalance < entryFeeSlap) {
      toast.error('Not enough $SLAP!', {
        description: `Need ${entryFeeSlap.toLocaleString()} $SLAP, have ${Math.floor(userBalance).toLocaleString()}`
      });
      setIsProcessingPayment(false); // Reset spinner immediately
      return;
    }
    
    // Get user's secret key for signing
    const userSecretKey = secretKey || localWallet?.secretKey;
    const userPublicKey = solanaAddress || localWallet?.publicKey;
    
    if (!userSecretKey || !userPublicKey) {
      toast.error('Wallet not connected!', {
        description: 'Please connect your wallet to play'
      });
      setIsProcessingPayment(false); // Reset spinner immediately
      return;
    }
    
    // ============================================================
    // CRITICAL: REAL ON-CHAIN TOKEN TRANSFER TO TREASURY
    // Game ONLY starts after successful blockchain confirmation!
    // ============================================================
    
    setIsProcessingPayment(true);
    setPaymentProgress({ step: 'checking', message: 'Checking balance...' });
    
    // Step 1: Verify balance on-chain
    const balanceCheck = await checkSlapBalance(userPublicKey, entryFeeSlap);
    if (!balanceCheck.hasEnough) {
      toast.error(`Insufficient $SLAP!`, {
        description: `Need ${entryFeeSlap.toLocaleString()} $SLAP, have ${Math.floor(balanceCheck.balance).toLocaleString()}`
      });
      setIsProcessingPayment(false);
      setPaymentProgress(null);
      return;
    }
    
    // Step 2: Execute REAL on-chain token transfer to Treasury
    setPaymentProgress({ step: 'transferring', message: `Transferring ${entryFeeSlap} $SLAP...` });
    
    console.log(`[SquidGame] 💰 REAL TRANSFER: ${entryFeeSlap} $SLAP → Treasury: ${TREASURY_WALLET}`);
    
    const transferResult = await transferToTreasury({
      amount: entryFeeSlap,
      userPublicKey: userPublicKey,
      secretKeyBase58: userSecretKey,
      onProgress: setPaymentProgress
    });
    
    setIsProcessingPayment(false);
    setPaymentProgress(null);
    
    if (!transferResult.success) {
      toast.error('Entry Fee Transfer Failed!', {
        description: transferResult.error || 'Transaction rejected'
      });
      return;
    }
    
    // ✅ Transaction confirmed! Now start the game
    console.log(`[SquidGame] ✅ CONFIRMED: ${entryFeeSlap} $SLAP → Treasury. Tx: ${transferResult.signature}`);
    setEntryTxSignature(transferResult.signature);
    
    // Refresh balance after transfer
    await fetchSlapTokenBalance(false);
    
    setEntryFeePaid(entryFeeSlap);
    setGameState('playing');
    setCurrentStep(0);
    setRevealedPanels({});
    setSelectedPanel(null);
    
    toast.success('🦑 GAME STARTED!', {
      description: `Entry: ${entryFeeSlap} $SLAP → Treasury (Tx: ${transferResult.signature?.slice(0, 8)}...)`
    });
  };
  
  // Generate safe panel for current step (50/50)
  const getSafePanelForStep = (step) => {
    // Use a deterministic but unpredictable method
    const hash = (step * 7919 + Date.now()) % 2;
    return hash === 0 ? 'left' : 'right';
  };
  
  // Choose a panel - with avatar animation
  const choosePanel = async (side) => {
    if (isRevealing || gameState !== 'playing') return;
    
    setSelectedPanel(side);
    setIsRevealing(true);
    
    // Animate avatar jumping to panel
    setAvatarPosition(side);
    
    // Dramatic pause while avatar "jumps"
    await new Promise(r => setTimeout(r, 1500));
    
    // Determine if safe (50/50 random)
    const safePanel = Math.random() < 0.5 ? 'left' : 'right';
    const isCorrect = side === safePanel;
    
    // Reveal the panels
    setRevealedPanels(prev => ({
      ...prev,
      [currentStep]: safePanel
    }));
    
    await new Promise(r => setTimeout(r, 500));
    
    if (isCorrect) {
      // SAFE - advance with green glow
      const newStep = currentStep + 1;
      setAvatarPosition('center'); // Reset avatar
      
      if (newStep >= TOTAL_STEPS) {
        // WON THE GAME!
        setGameState('won');
        const winnings = getPotentialWin(TOTAL_STEPS);
        
        // For now: Add credits to user balance (payout from house pool)
        await addCredits(winnings, 'squid_game_win_payout');
        
        console.log(`[SquidGame] 🏆 WINNER! Payout: ${winnings} $SLAP (from House Pool)`);
        console.log(`[SquidGame] Treasury: ${TREASURY_WALLET}`);
        
        // Celebration
        confetti({
          particleCount: 200,
          spread: 100,
          origin: { y: 0.6 },
          colors: ['#FFD700', '#00FF00', '#FF00FF']
        });
        
        toast.success('🏆 YOU SURVIVED!', {
          description: `Won ${winnings.toLocaleString()} $SLAP (${MULTIPLIERS[TOTAL_STEPS - 1]}x)! Payout credited.`
        });
      } else {
        setCurrentStep(newStep);
        toast.success(`✓ SAFE! Step ${newStep}/${TOTAL_STEPS}`, {
          description: `Current multiplier: ${getCurrentMultiplier()}x`
        });
      }
    } else {
      // TRAP - Glass shatters, avatar falls!
      setShowShatter(true);
      
      // Screen shake effect
      document.body.style.animation = 'shake 0.5s ease-in-out';
      setTimeout(() => {
        document.body.style.animation = '';
      }, 500);
      
      await new Promise(r => setTimeout(r, 800));
      setShowShatter(false);
      setGameState('lost');
      
      // Entry fee already transferred to Treasury on game start
      toast.error('💀 GLASS SHATTERED!', {
        description: `Lost ${entryFeePaid} $SLAP → House Treasury. You fell!`
      });
      
      console.log(`[SquidGame] 💰 House profit: ${entryFeePaid} $SLAP → Treasury: ${TREASURY_WALLET}`);
    }
    
    setIsRevealing(false);
    setSelectedPanel(null);
  };
  
  // Cash out early - REQUEST PAYOUT FROM TREASURY
  const cashOut = async () => {
    if (currentStep === 0) {
      toast.error("Can't cash out before first step!");
      return;
    }
    
    const currentMultiplier = getCurrentMultiplier();
    if (currentMultiplier === 0) {
      toast.error("This step has no payout! Continue to next multiplier step.");
      return;
    }
    
    const winnings = getPotentialWin(currentStep);
    
    // Log payout request to Treasury
    console.log(`[SquidGame] 💰 PAYOUT REQUEST: ${winnings} $SLAP`);
    console.log(`[SquidGame] Player: ${walletAddress}`);
    console.log(`[SquidGame] Treasury: ${TREASURY_WALLET}`);
    console.log(`[SquidGame] Multiplier: ${currentMultiplier}x`);
    
    // Add to local balance (instant feedback)
    await addCredits(winnings, 'squid_game_cashout');
    
    setGameState('won');
    toast.success('💰 CASHED OUT!', {
      description: `Won ${winnings.toLocaleString()} $SLAP (${currentMultiplier}x) - Payout pending from Treasury`
    });
  };
  
  // Reset game
  const resetGame = () => {
    setGameState('lobby');
    setCurrentStep(0);
    setRevealedPanels({});
    setSelectedPanel(null);
    setEntryFeePaid(0);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#1a0a0a] via-[#0a0a1a] to-black relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-600/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-red-600/10 rounded-full blur-[150px]" />
        {/* Grid pattern */}
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,0,100,0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(255,0,100,0.3) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>
      
      {/* Header */}
      <div className="relative z-10 pt-8 pb-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white/60 hover:text-white"
          >
            <Home className="w-5 h-5 mr-2" />
            Back
          </Button>
          
          <div className="text-center">
            <h1 className="font-anton text-4xl tracking-widest"
              style={{
                background: 'linear-gradient(to right, #FF0066, #FF6600)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 0 40px rgba(255,0,102,0.5)'
              }}
            >
              🦑 THE SLAP BRIDGE
            </h1>
            <p className="text-pink-400/80 text-sm mt-1">7 Steps • 50/50 Chance • Winner Takes All</p>
          </div>
          
          <div className="text-right">
            <p className="text-white/60 text-xs">Balance</p>
            <p className="font-anton text-xl text-yellow-400" data-testid="squid-balance">
              {connectionStatus === 'connecting' ? '---' : `${userBalance.toLocaleString()} $SLAP`}
            </p>
          </div>
        </div>
      </div>
      
      {/* Main Game Area */}
      <div className="relative z-10 px-6 py-8">
        <div className="max-w-4xl mx-auto">
          
          {/* CONNECTION ERROR STATE - No backup UI, just error + retry */}
          {connectionStatus === 'error' && (
            <motion.div
              className="fixed inset-0 z-[999] bg-black/95 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-center max-w-md mx-4">
                <div className="text-6xl mb-6">🔴</div>
                <h2 className="font-anton text-3xl text-red-400 mb-4">CONNECTION ERROR</h2>
                <p className="font-space text-white/70 mb-6">
                  Unable to connect to Solana network. Please check your connection and try again.
                </p>
                <Button
                  onClick={() => window.location.reload()}
                  className="px-8 py-4 bg-red-600 hover:bg-red-500 text-white font-anton text-xl rounded-xl"
                >
                  🔄 RETRY CONNECTION
                </Button>
                <p className="mt-4 text-white/40 text-sm">
                  Auto-retrying in background...
                </p>
              </div>
            </motion.div>
          )}
          
          {/* LOBBY STATE */}
          {gameState === 'lobby' && (
            <motion.div
              className="text-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Entry Fee Card */}
              <div className="bg-black/60 border-2 border-pink-500/50 rounded-3xl p-8 max-w-md mx-auto mb-8"
                style={{ boxShadow: '0 0 60px rgba(255,0,102,0.3)' }}
              >
                <Skull className="w-16 h-16 text-pink-500 mx-auto mb-4" />
                <h2 className="font-anton text-2xl text-white mb-2">ENTRY FEE</h2>
                
                {/* Dynamic Price Display */}
                <div className="bg-black/50 rounded-xl p-4 mb-4">
                  <p className="text-white/60 text-sm">$2.00 USD in $SLAP</p>
                  <p className="font-anton text-5xl text-pink-400">
                    {entryFeeSlap.toLocaleString()}
                  </p>
                  <p className="text-yellow-400 text-lg">$SLAP</p>
                  <div className="flex items-center justify-center gap-2 mt-2">
                    <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'}`} />
                    <span className="text-white/40 text-xs">
                      Price updates every 5s
                    </span>
                  </div>
                </div>
                
                {/* Multiplier Preview - NEW LADDER */}
                <div className="grid grid-cols-7 gap-1 mb-6">
                  {MULTIPLIERS.map((mult, i) => (
                    <div key={i} className="text-center">
                      <div className="text-xs text-white/40">S{i + 1}</div>
                      <div className={`text-sm font-bold ${mult > 0 ? 'text-green-400' : 'text-red-400/50'}`}>
                        {mult > 0 ? `${mult}x` : '—'}
                      </div>
                    </div>
                  ))}
                </div>
                
                {/* Simplified Info */}
                <p className="text-white/50 text-xs mb-6">
                  50/50 each step • Cash out at 1.2x, 2x, or go for 4x Jackpot!
                </p>
                
                {/* Payment Progress */}
                {isProcessingPayment ? (
                  <div className="mb-6">
                    <div className="flex items-center justify-center gap-3 mb-4">
                      <Loader2 className="w-8 h-8 text-green-400 animate-spin" />
                      <span className="font-anton text-xl text-green-400">PROCESSING PAYMENT...</span>
                    </div>
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-xl">
                      <p className="text-green-400 text-sm">{paymentProgress?.message || 'Connecting to Solana...'}</p>
                      <p className="text-white/50 text-xs mt-2">
                        💰 Real on-chain transfer to Treasury: {TREASURY_WALLET.slice(0, 12)}...
                      </p>
                    </div>
                  </div>
                ) : null}
                
                {/* Play Button */}
                <Button
                  onClick={startGame}
                  disabled={userBalance < entryFeeSlap || isProcessingPayment}
                  className="w-full py-6 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 text-white font-anton text-2xl rounded-xl disabled:opacity-50"
                  data-testid="start-squid-game-btn"
                >
                  {isProcessingPayment ? (
                    <>
                      <Loader2 className="w-6 h-6 mr-2 animate-spin" />
                      PROCESSING...
                    </>
                  ) : (
                    <>
                      <Play className="w-6 h-6 mr-2" />
                      ENTER THE BRIDGE
                    </>
                  )}
                </Button>
                
                {userBalance < entryFeeSlap && !isProcessingPayment && (
                  <div className="mt-4 p-3 bg-red-500/20 border border-red-500/30 rounded-xl">
                    <p className="text-red-400 text-sm mb-2">
                      Need {(entryFeeSlap - userBalance).toLocaleString()} more $SLAP
                    </p>
                    <button
                      onClick={() => navigate('/wallet')}
                      className="w-full py-2 bg-green-500 hover:bg-green-400 text-white font-bold rounded-lg text-sm"
                    >
                      💰 GET $SLAP →
                    </button>
                  </div>
                )}
              </div>
              
              {/* Game Rules */}
              <div className="text-white/60 text-sm max-w-md mx-auto">
                <p className="mb-2">📋 <strong>RULES:</strong></p>
                <p>• Choose LEFT or RIGHT glass panel</p>
                <p>• Safe = advance. Trap = eliminated</p>
                <p>• Cash out at steps with multipliers (1.2x, 2x, 4x)</p>
              </div>
            </motion.div>
          )}
          
          {/* PLAYING STATE */}
          {gameState === 'playing' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              {/* Progress Bar */}
              <div className="mb-8">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white/60">Progress</span>
                  <span className="font-anton text-xl text-pink-400">
                    Step {currentStep + 1} / {TOTAL_STEPS}
                  </span>
                </div>
                <div className="h-3 bg-black/50 rounded-full overflow-hidden border border-pink-500/30">
                  <motion.div
                    className="h-full bg-gradient-to-r from-pink-500 to-red-500"
                    initial={{ width: 0 }}
                    animate={{ width: `${(currentStep / TOTAL_STEPS) * 100}%` }}
                  />
                </div>
              </div>
              
              {/* Current Winnings */}
              <div className="text-center mb-8">
                <p className="text-white/60 text-sm">Current Winnings</p>
                <p className="font-anton text-4xl text-green-400">
                  {getPotentialWin(currentStep).toLocaleString()} $SLAP
                </p>
                <p className="text-yellow-400">
                  {currentStep > 0 ? `${getCurrentMultiplier()}x multiplier` : 'Complete step 1 to start winning'}
                </p>
              </div>
              
              {/* Avatar Selection - Only in lobby */}
              {currentStep === 0 && (
                <div className="flex items-center justify-center gap-3 mb-6">
                  <span className="text-white/60 text-sm">Choose Avatar:</span>
                  <div className="flex gap-2">
                    {AVATARS.map((avatar) => (
                      <button
                        key={avatar}
                        onClick={() => setSelectedAvatar(avatar)}
                        className={`text-2xl p-2 rounded-lg transition-all ${
                          selectedAvatar === avatar
                            ? 'bg-pink-500/50 ring-2 ring-pink-500'
                            : 'bg-white/10 hover:bg-white/20'
                        }`}
                      >
                        {avatar}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Glass Bridge Visual - GLASSMORPHISM */}
              <div className="relative mb-8">
                {/* Glass Shatter Overlay */}
                <AnimatePresence>
                  {showShatter && (
                    <motion.div
                      className="absolute inset-0 z-50 flex items-center justify-center"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <div className="text-8xl animate-bounce">💥</div>
                      <div className="absolute inset-0 bg-red-500/30 animate-pulse" />
                    </motion.div>
                  )}
                </AnimatePresence>
                
                {/* Avatar */}
                <motion.div
                  className="absolute top-0 left-1/2 transform -translate-x-1/2 z-40 text-5xl"
                  animate={{
                    x: avatarPosition === 'left' ? -60 : avatarPosition === 'right' ? 60 : 0,
                    y: showShatter ? 200 : (currentStep * 72),
                    rotate: showShatter ? 720 : 0,
                    scale: showShatter ? 0 : 1
                  }}
                  transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                >
                  {selectedAvatar}
                </motion.div>
                
                {/* Bridge Structure */}
                <div className="flex flex-col gap-4 items-center pt-16">
                  {Array.from({ length: TOTAL_STEPS }).map((_, stepIndex) => {
                    const isCurrentStep = stepIndex === currentStep;
                    const isPastStep = stepIndex < currentStep;
                    const revealed = revealedPanels[stepIndex];
                    const stepMultiplier = MULTIPLIERS[stepIndex];
                    
                    return (
                      <motion.div
                        key={stepIndex}
                        className={`flex gap-4 items-center ${isCurrentStep ? 'scale-110' : ''}`}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: stepIndex * 0.1 }}
                      >
                        {/* Left Glass Panel - PREMIUM GLASS TEAL */}
                        <motion.button
                          onClick={() => isCurrentStep && !isRevealing && choosePanel('left')}
                          disabled={!isCurrentStep || isRevealing}
                          className={`w-24 h-16 rounded-xl transition-all relative overflow-hidden ${
                            isPastStep
                              ? revealed === 'left'
                                ? 'bg-green-500/40 border-2 border-green-400 shadow-lg shadow-green-500/50'
                                : 'bg-red-500/20 border-2 border-red-500/50 opacity-30'
                              : isCurrentStep
                                ? selectedPanel === 'left'
                                  ? 'bg-teal-400/30 border-2 border-teal-300 animate-pulse shadow-lg shadow-teal-400/50'
                                  : 'backdrop-blur-xl bg-white/10 border-2 border-white/30 hover:bg-teal-400/20 hover:border-teal-300 cursor-pointer shadow-xl shadow-teal-500/20'
                                : 'backdrop-blur-sm bg-white/5 border border-white/10'
                          }`}
                          style={{
                            backdropFilter: isCurrentStep && !isPastStep ? 'blur(16px)' : 'none',
                            background: isCurrentStep && !isPastStep && selectedPanel !== 'left'
                              ? 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(20,184,166,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                              : undefined
                          }}
                          whileHover={isCurrentStep && !isRevealing ? { scale: 1.15, y: -8, rotateY: 10 } : {}}
                          whileTap={isCurrentStep && !isRevealing ? { scale: 0.95 } : {}}
                        >
                          {/* Glass shine effect - Premium */}
                          {isCurrentStep && !isPastStep && (
                            <>
                              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent pointer-events-none" />
                              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-teal-400/10 to-transparent pointer-events-none" />
                            </>
                          )}
                          {isPastStep && (
                            revealed === 'left' 
                              ? <span className="text-3xl drop-shadow-lg">✓</span>
                              : <span className="text-2xl opacity-50">💀</span>
                          )}
                          {isCurrentStep && !selectedPanel && (
                            <span className="font-bold text-white drop-shadow-lg text-lg">LEFT</span>
                          )}
                        </motion.button>
                        
                        {/* Step Number & Multiplier */}
                        <div className={`w-16 h-16 flex flex-col items-center justify-center font-anton ${
                          isPastStep ? 'text-green-400' : isCurrentStep ? 'text-yellow-400' : 'text-white/30'
                        }`}>
                          <span className="text-xl">{stepIndex + 1}</span>
                          <span className={`text-xs ${stepMultiplier > 0 ? 'text-green-400' : 'text-red-400/50'}`}>
                            {stepMultiplier > 0 ? `${stepMultiplier}x` : '—'}
                          </span>
                        </div>
                        
                        {/* Right Glass Panel - PREMIUM GLASS TEAL */}
                        <motion.button
                          onClick={() => isCurrentStep && !isRevealing && choosePanel('right')}
                          disabled={!isCurrentStep || isRevealing}
                          className={`w-24 h-16 rounded-xl transition-all relative overflow-hidden ${
                            isPastStep
                              ? revealed === 'right'
                                ? 'bg-green-500/40 border-2 border-green-400 shadow-lg shadow-green-500/50'
                                : 'bg-red-500/20 border-2 border-red-500/50 opacity-30'
                              : isCurrentStep
                                ? selectedPanel === 'right'
                                  ? 'bg-teal-400/30 border-2 border-teal-300 animate-pulse shadow-lg shadow-teal-400/50'
                                  : 'backdrop-blur-xl bg-white/10 border-2 border-white/30 hover:bg-teal-400/20 hover:border-teal-300 cursor-pointer shadow-xl shadow-teal-500/20'
                                : 'backdrop-blur-sm bg-white/5 border border-white/10'
                          }`}
                          style={{
                            backdropFilter: isCurrentStep && !isPastStep ? 'blur(16px)' : 'none',
                            background: isCurrentStep && !isPastStep && selectedPanel !== 'right'
                              ? 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, rgba(20,184,166,0.1) 50%, rgba(255,255,255,0.05) 100%)'
                              : undefined
                          }}
                          whileHover={isCurrentStep && !isRevealing ? { scale: 1.15, y: -8, rotateY: -10 } : {}}
                          whileTap={isCurrentStep && !isRevealing ? { scale: 0.95 } : {}}
                        >
                          {/* Glass shine effect - Premium */}
                          {isCurrentStep && !isPastStep && (
                            <>
                              <div className="absolute inset-0 bg-gradient-to-br from-white/30 via-transparent to-transparent pointer-events-none" />
                              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-gradient-to-t from-teal-400/10 to-transparent pointer-events-none" />
                            </>
                          )}
                          {isPastStep && (
                            revealed === 'right' 
                              ? <span className="text-3xl drop-shadow-lg">✓</span>
                              : <span className="text-2xl opacity-50">💀</span>
                          )}
                          {isCurrentStep && !selectedPanel && (
                            <span className="font-bold text-white drop-shadow-lg text-lg">RIGHT</span>
                          )}
                        </motion.button>
                      </motion.div>
                    );
                  })}
                </div>
                
                {/* Revealing Animation */}
                {isRevealing && (
                  <motion.div
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-xl"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div className="text-center">
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                      >
                        <Zap className="w-12 h-12 text-yellow-400" />
                      </motion.div>
                      <p className="font-anton text-xl text-white mt-2">REVEALING...</p>
                    </div>
                  </motion.div>
                )}
              </div>
              
              {/* Cash Out Button - Only enabled on payout steps */}
              {currentStep > 0 && (
                <motion.div
                  className="text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  {getCurrentMultiplier() > 0 ? (
                    <>
                      <Button
                        onClick={cashOut}
                        disabled={isRevealing}
                        className="px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-bold text-lg rounded-xl"
                        data-testid="cash-out-btn"
                      >
                        <DollarSign className="w-5 h-5 mr-2" />
                        CASH OUT ({getPotentialWin(currentStep).toLocaleString()} $SLAP)
                      </Button>
                      <p className="text-white/40 text-xs mt-2">
                        Take your winnings and leave safely
                      </p>
                    </>
                  ) : (
                    <div className="px-8 py-4 bg-gray-700/50 text-white/50 font-bold text-lg rounded-xl">
                      ⚠️ NO PAYOUT THIS STEP - CONTINUE
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          )}
          
          {/* WON STATE - Treasury Payout */}
          {gameState === 'won' && (
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <motion.div
                animate={{ y: [0, -20, 0] }}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <Trophy className="w-24 h-24 text-yellow-400 mx-auto mb-4" />
              </motion.div>
              <h2 className="font-anton text-5xl text-green-400 mb-4">YOU SURVIVED!</h2>
              <p className="text-white/80 text-xl mb-4">
                Won <span className="text-yellow-400 font-bold">
                  {getPotentialWin(currentStep === TOTAL_STEPS ? TOTAL_STEPS : currentStep).toLocaleString()}
                </span> $SLAP
              </p>
              
              {/* Treasury Payout Info */}
              <div className="bg-green-500/20 border border-green-500/50 rounded-xl p-4 max-w-md mx-auto mb-6">
                <p className="text-green-400 font-bold mb-2">💰 PAYOUT CREDITED</p>
                <p className="text-white/60 text-sm">
                  From Treasury: {TREASURY_WALLET.slice(0, 8)}...{TREASURY_WALLET.slice(-4)}
                </p>
                <p className="text-white/60 text-xs mt-1">
                  Tokens added to your balance
                </p>
              </div>
              
              <Button
                onClick={resetGame}
                className="px-8 py-4 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 text-white font-bold text-lg rounded-xl"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                PLAY AGAIN
              </Button>
            </motion.div>
          )}
          
          {/* LOST STATE */}
          {gameState === 'lost' && (
            <motion.div
              className="text-center"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
            >
              <motion.div
                animate={{ rotate: [0, 10, -10, 0] }}
                transition={{ duration: 0.5, repeat: 3 }}
              >
                <Skull className="w-24 h-24 text-red-500 mx-auto mb-4" />
              </motion.div>
              <h2 className="font-anton text-5xl text-red-400 mb-4">ELIMINATED</h2>
              <p className="text-white/80 text-xl mb-2">
                The glass shattered beneath you.
              </p>
              <p className="text-red-400 mb-8">
                <Flame className="inline w-5 h-5 mr-1" />
                Lost {entryFeePaid.toLocaleString()} $SLAP
              </p>
              <Button
                onClick={resetGame}
                className="px-8 py-4 bg-gradient-to-r from-pink-600 to-red-600 hover:from-pink-500 hover:to-red-500 text-white font-bold text-lg rounded-xl"
              >
                <RotateCcw className="w-5 h-5 mr-2" />
                TRY AGAIN
              </Button>
            </motion.div>
          )}
          
        </div>
      </div>
    </div>
  );
};

export default SquidGameBridge;
